import React, { forwardRef, useImperativeHandle } from 'react';
import { pdf, Document } from '@react-pdf/renderer';
import ReportTemplatePDF from './ReportTemplatePDF';
import FullReportTemplatePDF from './FullReportTemplatePDF';
import ExecutiveSummaryTemplatePDF from './ExecutiveSummaryTemplatePDF';
import ExecutiveSummaryTemplatePDF2 from './ExecutiveSummaryTemplatePDF2';
import ReportTemplatePDF2 from './ReportTemplatePDF2';
import FullReportTemplatePDF2 from './FullReportTemplatePDF2';
import CertificatePageV2 from './sections/v2/CertificatePageV2';
import LetterOfAttestationPDF from './sections/LetterOfAttestationPDF';
import { ReportData } from '../types/report.types';
import ChartImageGenerator from './ChartImageGenerator';
import { uploadProgramReportPDFToVanta } from '../../../utils/api/endpoints/program-reports/program-reports';
import { filterReportDataByRunId } from '../utils/findingsUtils';
import { registerReportFonts } from '../utils/reportSharedUtils';
import { preWarmImages } from './pdfHtmlImageUtils';

// Register fonts once globally
registerReportFonts();

interface PDFDownloaderProps {
  reportData: ReportData;
  fileName?: string;
  compact?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  processedFindings?: Record<string, any[]> | null;
  previewMode?: 'full' | 'technical' | 'executive' | 'attestation';
  pieChartImage?: string;
  barChartImage?: string;
}

const PDFDownloader = forwardRef<any, PDFDownloaderProps>(({
  reportData: rawReportData,
  fileName = 'security-report.pdf',
  compact = false,
  onClick,
  disabled = false,
  processedFindings,
  previewMode = 'full',
  pieChartImage: propPieChartImage,
  barChartImage: propBarChartImage,
}, ref) => {
  const reportData = React.useMemo(() => filterReportDataByRunId(rawReportData) || rawReportData, [rawReportData]);
  const [internalPieChart, setInternalPieChart] = React.useState<string | undefined>();
  const [internalBarChart, setInternalBarChart] = React.useState<string | undefined>();

  const pieChartImage = propPieChartImage || internalPieChart;
  const barChartImage = propBarChartImage || internalBarChart;

  const pieData = {
    labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
    datasets: [{
      data: [
        reportData.open_close_counts_by_severity?.Critical?.Total || 0,
        reportData.open_close_counts_by_severity?.High?.Total || 0,
        reportData.open_close_counts_by_severity?.Medium?.Total || 0,
        reportData.open_close_counts_by_severity?.Low?.Total || 0,
        reportData.open_close_counts_by_severity?.Info?.Total || 0,
      ],
      backgroundColor: ['#dc2626', '#ea580c', '#eab308', '#22c55e', '#64748b'],
    }],
  };

  const barData = {
    labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
    datasets: [{
      label: 'Number of Findings',
      data: [
        reportData.open_close_counts_by_severity?.Critical?.Total || 0,
        reportData.open_close_counts_by_severity?.High?.Total || 0,
        reportData.open_close_counts_by_severity?.Medium?.Total || 0,
        reportData.open_close_counts_by_severity?.Low?.Total || 0,
        reportData.open_close_counts_by_severity?.Info?.Total || 0,
      ],
      backgroundColor: ['#dc2626', '#ea580c', '#eab308', '#22c55e', '#64748b'],
    }],
  };

  const chartsReady = pieChartImage && barChartImage;

  // Use a ref to capture the latest chart image states to avoid closed-over stale state inside waitForCharts promise loops
  const pieChartRef = React.useRef(pieChartImage);
  const barChartRef = React.useRef(barChartImage);
  React.useEffect(() => {
    pieChartRef.current = pieChartImage;
  }, [pieChartImage]);
  React.useEffect(() => {
    barChartRef.current = barChartImage;
  }, [barChartImage]);

  const waitForCharts = async (maxMs = 5000, intervalMs = 100) => {
    const start = Date.now();
    while (!(pieChartRef.current && barChartRef.current)) {
      if (Date.now() - start > maxMs) break;
      await new Promise(r => setTimeout(r, intervalMs));
    }
  };

  const generatePDFBlob = async (): Promise<Blob> => {
    if (previewMode !== 'attestation') {
      await waitForCharts();
      if (!(pieChartRef.current && barChartRef.current)) {
        throw new Error('Charts not ready for PDF generation');
      }

      // Pre-warm image cache: convert all Azure Blob image URLs in findings HTML fields
      // to base64 data URIs before PDF rendering (fixes CORS blank-image issue)
      const allHtmlFields: (string | undefined | null)[] = [];
      allHtmlFields.push(reportData.scope, reportData.scope_notes);
      const findings = reportData.detailed_findings || [];
      for (const f of findings) {
        allHtmlFields.push(f.description, f.instructions, f.impact, f.fix, f.evidence);
      }
      await preWarmImages(allHtmlFields);
    }

    const isV2 = reportData.template === 'template-2';
    let documentNode;
    if (previewMode === 'attestation') {
      documentNode = (
        <Document>
          <LetterOfAttestationPDF reportData={reportData} />
        </Document>
      );
    } else if (previewMode === 'executive') {
      documentNode = isV2 ? (
        <ExecutiveSummaryTemplatePDF2
          reportData={reportData}
          pieChartImage={pieChartRef.current}
          barChartImage={barChartRef.current}
        />
      ) : (
        <ExecutiveSummaryTemplatePDF
          reportData={reportData}
        />
      );
    } else if (previewMode === 'technical') {
      documentNode = isV2 ? (
        <ExecutiveSummaryTemplatePDF2
          reportData={reportData}
          pieChartImage={pieChartRef.current}
          barChartImage={barChartRef.current}
        />
      ) : (
        <ReportTemplatePDF
          reportData={reportData}
          processedFindings={processedFindings}
          pieChartImage={pieChartRef.current}
          barChartImage={barChartRef.current}
        />
      );
    } else {
      documentNode = isV2 ? (
        <FullReportTemplatePDF2
          reportData={reportData}
          processedFindings={processedFindings}
          pieChartImage={pieChartRef.current}
          barChartImage={barChartRef.current}
        />
      ) : (
        <FullReportTemplatePDF
          reportData={reportData}
          processedFindings={processedFindings}
          pieChartImage={pieChartRef.current}
          barChartImage={barChartRef.current}
        />
      );
    }

    const blob = await pdf(documentNode).toBlob();
    return blob;
  };

  const downloadPDF = async () => {
    try {
      const blob = await generatePDFBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const uploadToVanta = async (programReportId: string, onProgress?: (p: number) => void) => {
    const blob = await generatePDFBlob();
    const file = new File([blob], fileName, { type: 'application/pdf' });
    return uploadProgramReportPDFToVanta(programReportId, file, onProgress);
  };

  useImperativeHandle(ref, () => ({
    downloadPDF,
    generatePDFBlob,
    uploadToVanta
  }));

  if (compact) {
    return (
      <>
        {!pieChartImage && (
          <ChartImageGenerator
            type="pie"
            data={pieData}
            onImageReady={setInternalPieChart}
          />
        )}
        {!barChartImage && (
          <ChartImageGenerator
            type="bar"
            data={barData}
            options={{
              scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
              plugins: { legend: { display: false } },
            }}
            onImageReady={setInternalBarChart}
          />
        )}
        <button
          onClick={onClick}
          title="Download PDF Report"
          className="flex items-center justify-center p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors shadow-sm"
          disabled={disabled || !chartsReady}
        >
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </button>
      </>
    );
  }

  return (
    <>
      {!pieChartImage && (
        <ChartImageGenerator
          type="pie"
          data={pieData}
          onImageReady={setInternalPieChart}
        />
      )}
      {!barChartImage && (
        <ChartImageGenerator
          type="bar"
          data={barData}
          options={{
            scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
            plugins: { legend: { display: false } },
          }}
          onImageReady={setInternalBarChart}
        />
      )}
    </>
  );
});

export default PDFDownloader;